import React from 'react';
import clsx from 'clsx';

/**
 * Visual tone for the empty-state panel. Each entity collection tints its
 * empty state to match its accent (servers neutral, agents cyan, skills amber,
 * virtual teal); `error` is the red failure panel.
 */
export type EmptyStateTone = 'neutral' | 'cyan' | 'amber' | 'teal' | 'error';

const TONE_CLASSES: Record<EmptyStateTone, string> = {
  neutral: 'bg-semi-color-fill-0 border border-dashed border-semi-color-border',
  cyan: 'bg-semi-color-fill-0 border border-dashed border-semi-color-border',
  amber: 'bg-semi-color-fill-0 border border-dashed border-semi-color-border',
  teal: 'bg-semi-color-fill-0 border border-dashed border-semi-color-border',
  error: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
};

interface EmptyStateProps {
  title: string;
  subtitle?: React.ReactNode;
  tone?: EmptyStateTone;
  /** Optional call-to-action button rendered under the message. */
  cta?: React.ReactNode;
  className?: string;
}

/**
 * The "No X found" panel shared by every entity collection on the Dashboard.
 * Replaces the repeated text-center / rounded / tinted blocks. The `error`
 * tone is used for load-failure messages (red title).
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  subtitle,
  tone = 'neutral',
  cta,
  className,
}) => {
  return (
    <div
      className={clsx('text-center py-12 rounded-lg', TONE_CLASSES[tone], className)}
    >
      <div
        className={clsx(
          'text-lg mb-2 font-medium',
          tone === 'error' ? 'text-red-500' : 'text-semi-color-text-1',
        )}
      >
        {title}
      </div>
      {subtitle && (
        <p
          className={clsx(
            'text-sm',
            tone === 'error'
              ? 'text-red-600 dark:text-red-400'
              : 'text-semi-color-text-2',
          )}
        >
          {subtitle}
        </p>
      )}
      {cta && <div className="mt-6">{cta}</div>}
    </div>
  );
};

export default EmptyState;
